import { signOut } from "@bindery/auth-consumer/browser";
import { useLogger } from "@bindery/logging/react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@bindery/ui/components/ui/alert-dialog";
import { Button } from "@bindery/ui/components/ui/button";
import { BookOpenIcon } from "@heroicons/react/24/solid";
import { useCallback, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { forgetLastReader, rememberLastReader } from "@/storage/last-reader";
import { clearUnconfirmed } from "@/storage/unconfirmed-reader";
import { confirmOfflineReader } from "@/sync/offline-reader";

export const WRONG_BOOKMARK_PATH = "/signed-out?reason=wrong-bookmark";
export const FORGET_TIMEOUT_MS = 5_000;

// Lazy: the route guard renders this gate, and must not open IndexedDB or
// the Convex client just to decide whether to show it.
const forgetLocalReader = (readerId: string): Promise<void> =>
  import("@/sync").then((sync) => sync.forgetLocalReader(readerId));

const FORGET_TIMED_OUT = Symbol("forget timed out");

function withTimeout(
  work: Promise<void>,
  ms: number,
): Promise<void | typeof FORGET_TIMED_OUT> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<typeof FORGET_TIMED_OUT>((resolve) => {
    timer = setTimeout(() => resolve(FORGET_TIMED_OUT), ms);
  });
  return Promise.race([work, timeout]).finally(() => clearTimeout(timer));
}

export interface BookmarkIdentityGateProps {
  /** The reader the bookmark signed in, from the session. */
  readerId: string;
  /** This device's nickname for that reader, if it has one. */
  nickname: string | null;
  forget?: (readerId: string) => Promise<void>;
  signOutTo?: (returnTo: string) => void;
  onConfirmed?: () => void;
  forgetTimeoutMs?: number;
}

/**
 * A printed bookmark signs in by readerId alone, so a sibling scanning the
 * wrong one would read into another child's study records. The dialog cannot
 * be dismissed by clicking outside: the child must pick "That's me" or "Not
 * me" before any reading.
 */
export function BookmarkIdentityGate({
  readerId,
  nickname,
  forget = forgetLocalReader,
  signOutTo = signOut,
  onConfirmed,
  forgetTimeoutMs = FORGET_TIMEOUT_MS,
}: BookmarkIdentityGateProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const log = useLogger();
  const [phase, setPhase] = useState<"asking" | "rejecting">("asking");
  const [confirmed, setConfirmed] = useState(false);

  const handleConfirm = useCallback(() => {
    log.event("reading.bookmark.identity_confirmed", { reader_id: readerId });
    clearUnconfirmed(readerId);
    rememberLastReader(readerId);
    confirmOfflineReader(readerId);
    setConfirmed(true);
    onConfirmed?.();
    void navigate(pathname, { replace: true });
  }, [log, readerId, navigate, pathname, onConfirmed]);

  const handleReject = useCallback(async () => {
    setPhase("rejecting");
    log.event("reading.bookmark.identity_rejected", { reader_id: readerId });
    try {
      const outcome = await withTimeout(forget(readerId), forgetTimeoutMs);
      if (outcome === FORGET_TIMED_OUT) {
        log.event("reading.bookmark.forget_timeout", {
          timeout_ms: forgetTimeoutMs,
        });
      }
    } catch (error) {
      log.error("reading.bookmark.forget_failed", error);
    }
    clearUnconfirmed(readerId);
    forgetLastReader(readerId);
    signOutTo(WRONG_BOOKMARK_PATH);
  }, [log, readerId, forget, signOutTo, forgetTimeoutMs]);

  return (
    <AlertDialog open={!confirmed}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {nickname ? `Welcome back, ${nickname}!` : "Is this your bookmark?"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {nickname
              ? "This bookmark opens your reading library. Let's make sure it's really you before we start."
              : "This bookmark opens a reader's library. Let's make sure it's yours before we start."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={() => void handleReject()}
            disabled={phase === "rejecting"}
          >
            {phase === "rejecting"
              ? "One moment..."
              : nickname
                ? `Not ${nickname}?`
                : "Not my bookmark"}
          </Button>
          <Button onClick={handleConfirm} disabled={phase === "rejecting"}>
            That&apos;s me
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function WrongBookmarkScreen() {
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 bg-white px-6 text-center dark:bg-gray-900">
      <BookOpenIcon className="h-16 w-16 text-indigo-400" />
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
        This bookmark belongs to another reader
      </h1>
      <p className="max-w-md text-gray-600 dark:text-gray-300">
        No worries — every reader has their own special bookmark. Ask your
        grown-up to help you find and scan yours, and your own stories will be
        waiting for you.
      </p>
      <Button asChild size="lg">
        <Link to="/">Go to the start page</Link>
      </Button>
    </div>
  );
}
